import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Alert,
  Divider,
} from "@mui/material";
import GavelIcon from "@mui/icons-material/Gavel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import DashboardLayout from "../../layout/DashboardLayout";
import { getContract, signContract } from "../../api/auth";
import { toast } from "react-toastify";
import { brand } from "../../theme";
import { EmptyState } from "../../components/EmptyState";

export default function ContractPage() {
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [signerName, setSignerName] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await getContract();
        setContract(res.data?.data);
      } catch (err) {
        console.error(err);
        setError("Failed to load contract status. Please refresh the page and try again.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleSign = async () => {
    if (!signerName.trim() || !agreed) return;
    setSubmitting(true);
    try {
      const res = await signContract({ signerName: signerName.trim(), agreed: true });
      setContract(res.data?.data);
      toast.success("Contract signed successfully.");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to sign contract. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </Box>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>
      </DashboardLayout>
    );
  }

  const isSigned = contract?.status === "Signed";
  const isPrepared = !!contract?.terms;

  return (
    <DashboardLayout>
      <Box maxWidth={720}>
        <Box display="flex" alignItems="center" gap={1.5} mb={3}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: "10px",
              background: brand.terracotta,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#fff",
              flexShrink: 0,
            }}
          >
            <GavelIcon sx={{ fontSize: "1.1rem" }} />
          </Box>
          <Box>
            <Typography variant="h5" color="#0f172a">Contract Agreement</Typography>
            <Typography variant="body2" color="#64748b">
              Review and sign your service agreement with SABRECWA.
            </Typography>
          </Box>
        </Box>

        {isSigned ? (
          <Paper sx={{ p: 3, border: "1px solid #bbf7d0", bgcolor: "#f0fdf4" }}>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <CheckCircleIcon sx={{ color: "#16a34a" }} />
              <Typography variant="subtitle1" fontWeight={700} color="#14532d">
                Contract Signed
              </Typography>
            </Box>
            <Divider sx={{ mb: 2.5 }} />
            <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
              <Box>
                <Typography variant="caption" color="#94a3b8">Signed By</Typography>
                <Typography variant="body2" fontWeight={500}>{contract.signerName}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="#94a3b8">Signed On</Typography>
                <Typography variant="body2" fontWeight={500}>
                  {new Date(contract.signedAt).toLocaleString("en-GB", {
                    dateStyle: "long",
                    timeStyle: "short",
                  })}
                </Typography>
              </Box>
            </Box>
          </Paper>
        ) : !isPrepared ? (
          <Paper sx={{ p: 3 }}>
            <EmptyState
              icon={<HourglassEmptyIcon sx={{ fontSize: "2.5rem", color: "#cbd5e1" }} />}
              title="Your contract isn't ready yet"
              description="Your sales/account specialist is preparing your contract terms. You'll get an email as soon as it's ready to review and sign."
            />
          </Paper>
        ) : (
          <Paper sx={{ p: 3 }}>
            <Typography
              variant="caption"
              color="#94a3b8"
              textTransform="uppercase"
              letterSpacing={1}
              fontWeight={600}
              display="block"
              mb={1.5}
            >
              Terms of Agreement
            </Typography>
            <Box
              sx={{
                maxHeight: 260,
                overflowY: "auto",
                p: 2,
                border: "1px solid #e2e8f0",
                borderRadius: "8px",
                bgcolor: "#f8fafc",
                whiteSpace: "pre-line",
                fontSize: "0.875rem",
                color: "#334155",
                mb: 3,
              }}
            >
              {contract.terms}
            </Box>

            <TextField
              label="Type your full legal name"
              value={signerName}
              onChange={(e) => setSignerName(e.target.value)}
              fullWidth
              sx={{ mb: 2 }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={agreed}
                  onChange={(e) => setAgreed(e.target.checked)}
                />
              }
              label="I have read and agree to the terms above"
              sx={{ mb: 2 }}
            />
            <Box>
              <Button
                variant="contained"
                disabled={!signerName.trim() || !agreed || submitting}
                onClick={handleSign}
              >
                {submitting ? "Signing…" : "Sign & Submit"}
              </Button>
            </Box>
          </Paper>
        )}
      </Box>
    </DashboardLayout>
  );
}
